export interface DirectoryNode {
  /** Full path. This is what the prefix filter takes. */
  path: string;
  /** Last segment, for display. */
  name: string;
  /** Nesting level, 0 for a top-level folder. */
  depth: number;
  /**
   * False for a folder that holds no assets itself and exists only because a
   * descendant does. Still selectable: the filter matches by prefix, so it
   * returns everything underneath.
   */
  hasAssets: boolean;
  /** True when at least one folder sits directly inside this one. */
  hasChildren: boolean;
}

/**
 * Turns the flat list of folder paths into a depth-first display order.
 *
 * The query returns only folders that directly hold an asset, so a path like
 * `/Archive/2024/Q3` arrives with no `/Archive` or `/Archive/2024` beside it.
 * Rendering that flat would show a child with no parent, so the intermediate
 * folders are synthesized here and marked `hasAssets: false`.
 *
 * Sorted per level rather than globally, so a folder sits with its siblings
 * instead of wherever its full path happens to fall alphabetically.
 */
/**
 * Every prefix of `path` that names a folder, outermost first, spelled the way
 * the source stored it. Providers disagree on the leading slash (Orange Logic
 * stores `Root/Library`, Shade `/Campaigns`), and the grid filters by equality
 * on the stored value, so a rebuilt path matches no rows.
 */
function directoryPathPrefixes(path: string): string[] {
  const segments = path.split("/");
  const prefixes: string[] = [];
  for (let i = 1; i <= segments.length; i++) {
    const prefix = segments.slice(0, i).join("/");
    // The empty segment ahead of a leading slash is the root, not a folder.
    if (prefix !== "") {
      prefixes.push(prefix);
    }
  }
  return prefixes;
}

export function buildDirectoryTree(paths: string[]): DirectoryNode[] {
  const withAssets = new Set(paths);
  const bySegments = new Map<string, string[]>();

  for (const path of paths) {
    // Register every ancestor, so an intermediate folder gets a row even when
    // nothing sits in it directly.
    for (const ancestorPath of directoryPathPrefixes(path)) {
      bySegments.set(ancestorPath, ancestorPath.split("/").filter(Boolean));
    }
  }

  const parentPaths = new Set(
    [...bySegments.keys()].map((path) => parentDirectoryPath(path)),
  );

  return [...bySegments.entries()]
    .sort(([, a], [, b]) => {
      const shared = Math.min(a.length, b.length);
      for (let i = 0; i < shared; i++) {
        const comparison = (a[i] ?? "").localeCompare(b[i] ?? "");
        if (comparison !== 0) return comparison;
      }
      // One is an ancestor of the other, so the shallower comes first.
      return a.length - b.length;
    })
    .map(([path, segments]) => ({
      path,
      name: segments.at(-1) ?? "",
      depth: segments.length - 1,
      hasAssets: withAssets.has(path),
      hasChildren: parentPaths.has(path),
    }));
}

/** The folder that holds `path`, or null for a top-level folder. */
export function parentDirectoryPath(path: string): string | null {
  const cut = path.lastIndexOf("/");
  return cut <= 0 ? null : path.slice(0, cut);
}

/** Every ancestor of `path`, outermost first, excluding `path` itself. */
export function directoryAncestorPaths(path: string): string[] {
  return directoryBreadcrumbs(path)
    .slice(0, -1)
    .map((crumb) => crumb.path);
}

/**
 * The rows the sidebar shows: a folder is visible only when every folder
 * above it is expanded, so a collapsed folder hides its whole subtree.
 */
export function visibleDirectoryNodes(
  tree: DirectoryNode[],
  isExpanded: (path: string) => boolean,
): DirectoryNode[] {
  return tree.filter((node) =>
    directoryAncestorPaths(node.path).every((ancestor) => isExpanded(ancestor)),
  );
}

/**
 * The folders sitting directly inside `parentPath`, or the top-level folders
 * when it is null.
 *
 * This is what the grid shows as folder tiles: one level at a time, the way a
 * file browser does.
 */
export function childDirectoryNodes(
  tree: DirectoryNode[],
  parentPath: string | null,
): DirectoryNode[] {
  return tree.filter((node) => parentDirectoryPath(node.path) === parentPath);
}

/** Every ancestor of `path`, outermost first, including `path` itself. */
export function directoryBreadcrumbs(path: string): DirectoryCrumb[] {
  return directoryPathPrefixes(path).map((prefix) => ({
    path: prefix,
    name: prefix.split("/").filter(Boolean).at(-1) ?? "",
  }));
}

export interface DirectoryCrumb {
  path: string;
  name: string;
}

/**
 * Which folders the sidebar shows open, and the selection it was last
 * reconciled against so a change in either can be detected without an effect.
 */
export interface DirectoryExpansion {
  forCollection: string | null;
  forDirectory: string | null;
  expanded: ReadonlySet<string>;
}

/** The folder and every folder above it, or nothing when no folder is open. */
function directoryPathFolders(directory: string | null): string[] {
  return directory === null
    ? []
    : directoryBreadcrumbs(directory).map((crumb) => crumb.path);
}

export function initialDirectoryExpansion(
  collection: string | null,
  directory: string | null,
): DirectoryExpansion {
  return {
    forCollection: collection,
    forDirectory: directory,
    expanded: new Set(directoryPathFolders(directory)),
  };
}

/**
 * Folds the current selection into the open set. Selecting a folder opens it
 * and its ancestors and closes every subtree that is neither on that path nor
 * under the selected folder, so moving to a sibling swaps which one is open
 * and moving up to an ancestor keeps the deeper path visible. Deselecting
 * leaves the set alone. A different collection has its own folders, so its
 * tree starts closed. Returns `previous` itself when nothing changed.
 */
export function reconcileDirectoryExpansion(
  previous: DirectoryExpansion,
  collection: string | null,
  directory: string | null,
): DirectoryExpansion {
  if (
    previous.forCollection === collection &&
    previous.forDirectory === directory
  ) {
    return previous;
  }
  const carried =
    previous.forCollection !== collection
      ? []
      : directory === null
        ? [...previous.expanded]
        : [...previous.expanded].filter((path) =>
            path.startsWith(`${directory}/`),
          );
  return {
    forCollection: collection,
    forDirectory: directory,
    expanded: new Set([...carried, ...directoryPathFolders(directory)]),
  };
}

export function toggleDirectoryExpansion(
  previous: DirectoryExpansion,
  path: string,
): DirectoryExpansion {
  const expanded = new Set(previous.expanded);
  if (expanded.has(path)) {
    expanded.delete(path);
  } else {
    expanded.add(path);
  }
  return { ...previous, expanded };
}
